// ═══════════════════════════════════════════════════════════════════════════
// CONSEILLER AGRICOLE - Widget Cockpit
// ═══════════════════════════════════════════════════════════════════════════
// Farm-Booking - Papy 73+ - Joshua Edition 🎮
// "Pas de blabla, que des ACTIONS !"
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Droplets, Thermometer, Leaf, Package, 
  RefreshCw, ChevronDown, ChevronUp, Clock, Zap
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Carte Conseil individuelle
// ═══════════════════════════════════════════════════════════════════════════
const CarteConseil = ({ conseil }) => {
  // Couleur selon priorité
  const getPrioriteStyle = () => {
    switch (conseil.priorite) {
      case 1: return 'bg-red-50 border-red-400 text-red-800';
      case 2: return 'bg-orange-50 border-orange-400 text-orange-800';
      case 3: return 'bg-yellow-50 border-yellow-400 text-yellow-800';
      default: return 'bg-blue-50 border-blue-400 text-blue-800';
    }
  };

  const getPrioriteLabel = () => {
    switch (conseil.priorite) {
      case 1: return 'URGENT';
      case 2: return 'IMPORTANT';
      case 3: return 'À PRÉVOIR';
      default: return 'INFO';
    }
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-3 ${getPrioriteStyle()}`}>
      <div className="flex items-start gap-3">
        {/* Icône */}
        <span className="text-2xl flex-shrink-0">{conseil.icone}</span>
        
        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
              conseil.priorite === 1 ? 'bg-red-200 text-red-800' :
              conseil.priorite === 2 ? 'bg-orange-200 text-orange-800' :
              'bg-yellow-200 text-yellow-800'
            }`}>
              {getPrioriteLabel()}
            </span>
            <span className="font-bold text-sm">{conseil.titre}</span>
          </div>
          <p className="text-sm">{conseil.message}</p>
          {conseil.action && (
            <p className="text-xs font-semibold mt-1 opacity-80">
              → {conseil.action}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : Widget Conseiller
// ═══════════════════════════════════════════════════════════════════════════
const ConseillerWidget = () => {
  const [conseils, setConseils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Charger les conseils
  const loadConseils = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/conseiller/du-jour`);
      const data = await response.json();
      
      if (data.success) {
        setConseils(data.conseils || []);
        setLastUpdate(new Date());
        setError(null);
      } else {
        setError(data.error);
      }
    } catch (err) {
      console.error('Erreur chargement conseils:', err);
      setError('Impossible de charger les conseils');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConseils();
    // Rafraîchir toutes les 15 minutes
    const interval = setInterval(loadConseils, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Compter par priorité
  const urgents = conseils.filter(c => c.priorite === 1).length;
  const importants = conseils.filter(c => c.priorite === 2).length;
  const autres = conseils.filter(c => c.priorite > 2).length;

  // Si pas de conseils
  if (!loading && conseils.length === 0) {
    return (
      <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg p-4 text-white mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Leaf className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold">✅ Tout va bien !</h3>
            <p className="text-green-100 text-sm">Aucune action urgente aujourd'hui</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-lg mb-6 overflow-hidden ${
      urgents > 0 ? 'bg-gradient-to-r from-red-600 to-red-500' :
      importants > 0 ? 'bg-gradient-to-r from-orange-500 to-amber-500' :
      'bg-gradient-to-r from-yellow-500 to-amber-400'
    }`}>
      
      {/* Header - Toujours visible */}
      <div 
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              {urgents > 0 ? (
                <AlertTriangle className="w-6 h-6 animate-pulse" />
              ) : (
                <Zap className="w-6 h-6" />
              )}
            </div>
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                🌅 Conseils du Jour
                {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              </h3>
              <div className="flex items-center gap-3 text-sm opacity-90">
                {urgents > 0 && (
                  <span className="bg-red-800/50 px-2 py-0.5 rounded">
                    🔴 {urgents} urgent{urgents > 1 ? 's' : ''}
                  </span>
                )}
                {importants > 0 && (
                  <span className="bg-orange-800/50 px-2 py-0.5 rounded">
                    🟠 {importants} important{importants > 1 ? 's' : ''}
                  </span>
                )}
                {autres > 0 && (
                  <span className="bg-yellow-800/50 px-2 py-0.5 rounded">
                    🟡 {autres} à prévoir
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {lastUpdate && (
              <span className="text-xs opacity-70 hidden sm:block">
                <Clock className="w-3 h-3 inline mr-1" />
                {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); loadConseils(); }}
              className="p-1.5 bg-white/20 rounded hover:bg-white/30 transition"
              title="Rafraîchir"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="p-1.5 bg-white/20 rounded">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>
        </div>
      </div>

      {/* Liste des conseils - Dépliable */}
      {expanded && (
        <div className="bg-white p-4">
          {error ? (
            <div className="text-red-600 text-center py-4">
              ⚠️ {error}
            </div>
          ) : loading ? (
            <div className="text-gray-500 text-center py-4">
              Analyse en cours...
            </div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {conseils.map((conseil, idx) => (
                <CarteConseil key={idx} conseil={conseil} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConseillerWidget;